/*
 * `NoContextLink` mimics the functionality of `Link`, but does not need to be rendered within
 *  the router to work properly.
 *
 * tldr; This is a Link that works outside the router.
 */

import isString from 'lodash/isString'
import React from 'react'

import history from '../history'

const isModifiedEvent = (e: React.MouseEvent<HTMLElement>) =>
  !!(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey)

interface NoContextLinkProps {
  children: React.ReactNode
  className?: string
  onClick?: (args: unknown) => void
  to: string
}

const NoContextLink = React.forwardRef<HTMLAnchorElement, NoContextLinkProps>(
  function NoContextLink(props, ref) {
    const {children, className = '', onClick = () => null, to} = props

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      onClick(e)

      const href = e.currentTarget.getAttribute('href')

      if (
        !e.defaultPrevented && // onClick prevented default
        e.button === 0 && // ignore right clicks
        !isModifiedEvent(e) && // ignore clicks with modifier keys
        isString(href) &&
        href.length
      ) {
        e.preventDefault()
        history.push(href)
      }
    }

    return (
      <a
        className={className}
        href={to}
        onClick={handleClick}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {children}
      </a>
    )
  },
)

export default NoContextLink
